import { useRef, useState } from 'react';
import useApiSubmit from '@/hooks/useApiSubmit';
import useProfile from '@/hooks/useProfileV2';
import checkinReports, { PostCommentApi } from '@/services/checkinReports';
import { AnswerTypes, Comment, QuestionOption, TReaction } from '@/types/api';
import { getComparator } from '@/utils/order';
import QuestionItem, { QuestionItemComponentProps } from './QuestionItem.component';

type QuestionItemComponentType = Pick<
  QuestionItemComponentProps,
  'withoutLabel' | 'isFinished' | 'isPriority'
>;

type QuestionItemContainerProps = QuestionItemComponentType & {
  label: string;
  answerType: AnswerTypes;
  answer: string;
  answerId: string;
  checkinId: string;
  description?: string;
  comments?: Comment[];
  questionOptions?: QuestionOption[];
  reactions?: TReaction[];
  refetchCheckin: () => void;
};

const QuestionItemContainer = function ({
  label,
  description,
  answerType,
  answer,
  answerId,
  isFinished,
  checkinId,
  comments = [],
  questionOptions,
  reactions,
  withoutLabel,
  refetchCheckin,
  isPriority,
}: QuestionItemContainerProps): JSX.Element {
  const commentsSorted = comments.sort(getComparator<Comment>('asc', 'created_at'));
  const { profile } = useProfile();
  const refCommentInput = useRef<HTMLInputElement | null>(null);

  const postCommentMutation = useApiSubmit<Comment, PostCommentApi>(checkinReports.postComment);

  const [isShowingInput, setIsShowingInput] = useState(false);

  const handleShowInput = (): void => {
    if (!comments.length) {
      setIsShowingInput((current) => !current);
      return;
    }

    refCommentInput.current?.focus();
  };

  const handlePostComment = async (comment?: string): Promise<void> => {
    if (comment && profile.id) {
      const newCommentObject = {
        text: comment,
        userId: profile.id,
        answerId,
      };

      await postCommentMutation.mutateAsync({ checkinId, comment: newCommentObject });
      refetchCheckin();
      setIsShowingInput(false);
    }
  };

  return (
    <QuestionItem
      isPriority={isPriority}
      isFinished={isFinished}
      withoutLabel={withoutLabel}
      refetchCheckin={refetchCheckin}
      reactions={reactions}
      label={label}
      description={description}
      answerType={answerType}
      questionOptions={questionOptions}
      answer={answer}
      answerId={answerId}
      comments={commentsSorted}
      isShowingInput={isShowingInput}
      handleShowInput={handleShowInput}
      handlePostComment={handlePostComment}
      refCommentInput={refCommentInput}
    />
  );
};

export default QuestionItemContainer;
